/* bubble-wrap/index.js — DOM orchestrator for the Virtual Bubble Wrap tool.
 *
 * The only DOM-coupled module. The rAF loop runs ONLY while bubbles are
 * animating (popping, ring fade, or refill pending); an idle sheet is static.
 */

import { buildSheet, hitTest, popAllOrder, pitchForRadius, REFILL_DELAY } from './sheet-model.js';
import { drawSheet, getTheme, THEME_NAMES } from './bubble-render.js';
import { dpr, reducedMotion } from '../toy-fx/prefs.js';
import { initFunnel } from '../toy-fx/telemetry.js';
import * as audio from '../toy-fx/audio.js';
import * as haptics from '../toy-fx/haptics.js';

const SLUG = 'bubble-wrap';
const POP_MS = 170;
const RING_MS = 420;

const I18N = (typeof window !== 'undefined' && window.BubbleWrapI18n) || {};
const t = (k, f) => (I18N && I18N[k]) || f || k;
const $ = (id) => document.getElementById(id);
function ae(ev, p) { try { if (window.__ae) window.__ae(ev, Object.assign({ slug: SLUG }, p || {})); } catch (_) {} }

export function mount() {
    const stage = $('bw-stage');
    const canvas = $('bw-canvas');
    if (!stage || !canvas) return;
    const ctx = canvas.getContext('2d');

    const state = {
        bubbles: [],
        anim: {},                 // id → { state, pop, ring, popStart, refillAt }
        theme: 'classic',
        layout: 'grid',
        size: 'medium',
        calm: false,
        sound: true,
        endless: true,
        sessionPops: 0,
        lifetimePops: 0,
        pendingTelemetry: 0,
        completed: false,
    };
    let rafId = 0, lastFrame = 0, w = 0, h = 0;
    let telemetryTimer = 0;

    /* ---- settings ---- */
    function restore() {
        try {
            const ts = window.__toolSettings; if (!ts) return;
            state.theme = ts.load(SLUG, 'theme', 'classic');
            state.layout = ts.load(SLUG, 'layout', 'grid');
            state.size = ts.load(SLUG, 'size', 'medium');
            state.calm = ts.load(SLUG, 'calm', '0') === '1';
            state.sound = ts.load(SLUG, 'sound', '1') !== '0';
            state.endless = ts.load(SLUG, 'endless', '1') !== '0';
            state.lifetimePops = parseInt(ts.load(SLUG, 'lifetime', '0'), 10) || 0;
        } catch (_) {}
    }
    function save(k, v) { try { window.__toolSettings && window.__toolSettings.save(SLUG, k, v); } catch (_) {} }

    /* ---- sheet ---- */
    function rebuild() {
        state.bubbles = buildSheet({ width: w, height: h, size: state.size, layout: state.layout });
        state.anim = {};
        for (let i = 0; i < state.bubbles.length; i++) {
            state.anim[state.bubbles[i].id] = { state: 'intact', pop: 0, ring: 0, popStart: 0, refillAt: 0 };
        }
        updateCount();
        render();
    }

    function resize() {
        const rect = stage.getBoundingClientRect();
        const nw = Math.max(120, rect.width);
        const nh = Math.max(120, rect.height);
        const changed = Math.round(nw) !== Math.round(w) || Math.round(nh) !== Math.round(h);
        w = nw;
        h = nh;
        const r = dpr();
        canvas.width = Math.round(w * r);
        canvas.height = Math.round(h * r);
        canvas.style.width = w + 'px';
        canvas.style.height = h + 'px';
        ctx.setTransform(r, 0, 0, r, 0, 0);
        if (changed) rebuild();
        else render();
    }

    /* ---- render ---- */
    function render() {
        try { drawSheet(ctx, state.bubbles, state.anim, getTheme(state.theme), w, h); }
        catch (e) { if (window.__handleError) window.__handleError(e, 'bubble-wrap.render'); }
    }

    /* ---- rAF (only while animating) ---- */
    function ensureLoop() {
        if (rafId) return;
        lastFrame = performance.now();
        rafId = requestAnimationFrame(frame);
    }
    function frame(now) {
        rafId = 0;
        let busy = false;
        try {
            for (let i = 0; i < state.bubbles.length; i++) {
                const id = state.bubbles[i].id;
                const a = state.anim[id];
                if (!a) continue;
                if (a.state === 'popped') {
                    if (a.pop < 1) { a.pop = Math.min(1, (now - a.popStart) / POP_MS); busy = true; }
                    if (a.ring < 1) { a.ring = Math.min(1, (now - a.popStart) / RING_MS); busy = true; }
                    if (a.refillAt && now >= a.refillAt) {
                        a.state = 'intact'; a.pop = 0; a.ring = 0; a.refillAt = 0;
                    } else if (a.refillAt) {
                        busy = true;
                    }
                }
            }
            render();
        } catch (e) {
            if (window.__handleError) window.__handleError(e, 'bubble-wrap.frame');
        }
        if (busy) { rafId = requestAnimationFrame(frame); }
    }

    /* ---- popping ---- */
    function popBubble(idx, now, opts) {
        const b = state.bubbles[idx];
        if (!b) return false;
        const a = state.anim[b.id];
        if (!a || a.state === 'popped') return false;
        a.state = 'popped';
        a.pop = 0;
        a.ring = 0;
        a.popStart = now;
        a.refillAt = state.endless ? now + (state.calm ? REFILL_DELAY * 1.8 : REFILL_DELAY) : 0;
        state.sessionPops++;
        if (state.sessionPops === 30) {
            try { window.__aeToolComplete && window.__aeToolComplete({ pops: state.sessionPops }); } catch (_) {}
        }
        state.lifetimePops++;
        state.pendingTelemetry++;
        if (state.sound) audio.popSound(pitchForRadius(b.r) * (state.calm ? 0.85 : 1));
        haptics.vibrate(state.calm ? 'soft' : 'pop');
        updateCount();
        scheduleTelemetry();
        if (state.lifetimePops % 100 === 0) {
            if (!(opts && opts.silentMilestone) && window.__toast) {
                window.__toast(t('milestone', '{n} bubbles popped!').replace('{n}', state.lifetimePops), 'success');
            }
            ae('feature_use', { f: 'pop_milestone', n: state.lifetimePops });
        }
        return true;
    }

    function popAt(clientX, clientY) {
        const rect = canvas.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        const idx = hitTest(state.bubbles, x, y);
        if (idx < 0) return;
        audio.unlock();
        if (popBubble(idx, performance.now())) ensureLoop();
    }

    function popAll() {
        audio.unlock();
        const order = popAllOrder(state.bubbles, w / 2, h / 2);
        const reduced = reducedMotion();
        let delay = 0;
        if (reduced) {
            const before = state.lifetimePops;
            order.forEach((idx) => { popBubble(idx, performance.now(), { silentMilestone: true }); });
            ensureLoop();
            // Single summary toast for the highest hundred-milestone the cascade crossed.
            const lastMilestone = Math.floor(state.lifetimePops / 100) * 100;
            if (lastMilestone > before && lastMilestone >= 100 && window.__toast) {
                window.__toast(t('milestone', '{n} bubbles popped!').replace('{n}', lastMilestone), 'success');
            }
        } else {
            order.forEach((idx) => {
                const fire = () => { popBubble(idx, performance.now()); ensureLoop(); };
                setTimeout(fire, delay); delay += state.calm ? 26 : 12;
            });
        }
        ae('feature_use', { f: 'pop_all' });
    }

    function resetSheet() {
        rebuild();
        ae('feature_use', { f: 'reset_sheet' });
        if (window.__announce) window.__announce(t('reset_done', 'Fresh sheet ready'));
    }

    /* ---- counter / telemetry ---- */
    function updateCount() {
        const sEl = $('bw-count');
        if (sEl) sEl.textContent = t('session_pops', 'Popped: {n}').replace('{n}', state.sessionPops);
        const lEl = $('bw-lifetime');
        if (lEl) lEl.textContent = t('lifetime_pops', 'All-time: {n}').replace('{n}', state.lifetimePops);
    }
    function scheduleTelemetry() {
        save('lifetime', String(state.lifetimePops));
        if (telemetryTimer) return;
        telemetryTimer = setTimeout(() => {
            telemetryTimer = 0;
            if (state.pendingTelemetry > 0) {
                ae('feature_use', { f: 'pop', count: state.pendingTelemetry });
                state.pendingTelemetry = 0;
            }
        }, 1500);
    }

    /* ---- wiring ---- */
    function wire() {
        let dragging = false;
        canvas.addEventListener('pointerdown', (e) => { dragging = true; popAt(e.clientX, e.clientY); });
        canvas.addEventListener('pointermove', (e) => { if (dragging) popAt(e.clientX, e.clientY); });
        const stop = () => { dragging = false; };
        canvas.addEventListener('pointerup', stop);
        canvas.addEventListener('pointercancel', stop);
        canvas.addEventListener('pointerleave', stop);

        bindClick('bw-pop-all', popAll);
        bindClick('bw-reset', resetSheet);
        bindSelect('bw-theme', 'theme', () => { save('theme', state.theme); render(); ae('feature_use', { f: 'theme_change' }); });
        bindSelect('bw-layout', 'layout', () => { save('layout', state.layout); rebuild(); ae('feature_use', { f: 'layout_change' }); });
        bindSelect('bw-size', 'size', () => { save('size', state.size); rebuild(); });
        bindToggle('bw-calm', 'calm', () => { save('calm', state.calm ? '1' : '0'); ae('feature_use', { f: 'calm_toggle' }); });
        bindToggle('bw-sound', 'sound', () => { save('sound', state.sound ? '1' : '0'); audio.setMuted(!state.sound); });
        bindToggle('bw-endless', 'endless', () => { save('endless', state.endless ? '1' : '0'); });
        audio.setMuted(!state.sound);

        // Keyboard: a focused canvas pops a random intact bubble.
        canvas.addEventListener('keydown', (e) => {
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                audio.unlock();
                const intact = state.bubbles.filter((b) => state.anim[b.id].state === 'intact');
                if (intact.length) {
                    const b = intact[(Math.random() * intact.length) | 0];
                    popBubble(state.bubbles.indexOf(b), performance.now());
                    ensureLoop();
                }
            }
        });

        window.addEventListener('resize', debounce(resize, 140));
        window.addEventListener('beforeunload', () => { if (rafId) cancelAnimationFrame(rafId); });
    }
    function bindClick(id, fn) { const el = $(id); if (el) el.addEventListener('click', fn); }
    function bindSelect(id, key, fn) {
        const el = $(id); if (!el) return;
        el.value = state[key];
        el.addEventListener('change', () => { state[key] = el.value; fn(); });
    }
    function bindToggle(id, key, fn) {
        const el = $(id); if (!el) return;
        el.checked = !!state[key];
        el.addEventListener('change', () => { state[key] = el.checked; fn(); });
    }

    /* ---- boot ---- */
    restore();
    wire();
    resize();
    try { initFunnel(SLUG); } catch (_) {}

    if (location.search.indexOf('bwdebug') >= 0) {
        window.__bwDebug = {
            state: state,
            popAll: popAll,
            popIndex: (i) => { popBubble(i, performance.now()); ensureLoop(); },
            intactCount: () => state.bubbles.filter((b) => state.anim[b.id].state === 'intact').length,
        };
    }
}

function debounce(fn, ms) {
    let timer = 0;
    return function () { clearTimeout(timer); timer = setTimeout(fn, ms); };
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount);
    else mount();
}
